import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Separator } from "../components/ui/separator";
import { Truck, CreditCard, ShieldCheck } from "@phosphor-icons/react";

export default function CheckoutPage() {
  const { cart, cartLoading } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [address, setAddress] = useState({
    street: "", city: "", state: "", zip: "", country: "US"
  });
  const [quote, setQuote] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  useEffect(() => {
    if (!cart.total) return;
    API.get("/shipping/quote", { params: { subtotal: cart.total } })
      .then(r => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [cart.total]);

  useEffect(() => {
    if (!promo || !cart.total) return;
    API.post("/discount/validate", { code: promo.code, subtotal: cart.total })
      .then(r => {
        if (r.data.valid) setPromo(r.data);
        else { setPromo(null); setPromoError(r.data.reason); }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.total]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const { data } = await API.post("/discount/validate", { code: promoInput, subtotal: cart.total });
      if (data.valid) {
        setPromo(data);
        setPromoInput("");
      } else {
        setPromoError(data.reason || "Invalid promo code");
      }
    } catch {
      setPromoError("Could not validate code. Please try again.");
    }
    setPromoLoading(false);
  };

  const summary = promo || quote;

  useEffect(() => {
    if (authLoading || cartLoading) return;
    if (!user || user === false) {
      navigate("/auth?redirect=checkout", { replace: true });
      return;
    }
    if (cart.items.length === 0) {
      navigate("/cart", { replace: true });
      return;
    }
    setReady(true);
  }, [authLoading, cartLoading, user, cart, navigate]);

  const handleCheckout = async () => {
    if (!address.street || !address.city || !address.state || !address.zip) {
      setError("Please fill in all shipping fields");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const origin = window.location.origin;
      const { data } = await API.post("/checkout", {
        origin_url: origin,
        shipping_address: address,
        discount_code: promo ? promo.code : null,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(" ") : "Checkout failed. Please try again.");
    }
    setLoading(false);
  };

  if (!ready) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-center">
        <p className="text-[#A1A1AA] text-sm font-light">Loading checkout...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="checkout-page">
      <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Secure Checkout</p>
      <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tighter mb-12">
        Checkout
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-8">
          {/* Trust badges */}
          <div className="flex flex-wrap gap-8 mb-4">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-xs tracking-wide">
              <ShieldCheck size={16} weight="thin" className="text-[#D4AF37]" /> Secure Payment
            </div>
            <div className="flex items-center gap-2 text-[#A1A1AA] text-xs tracking-wide">
              <Truck size={16} weight="thin" className="text-[#D4AF37]" /> Fast Shipping
            </div>
            <div className="flex items-center gap-2 text-[#A1A1AA] text-xs tracking-wide">
              <CreditCard size={16} weight="thin" className="text-[#D4AF37]" /> Powered by Square
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8">
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7] mb-6">Shipping Address</h2>
            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Street Address</label>
                <input type="text" value={address.street} onChange={e => setAddress(p => ({ ...p, street: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light" placeholder="123 Luxury Lane" data-testid="shipping-street" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">City</label>
                  <input type="text" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light" placeholder="City" data-testid="shipping-city" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">State</label>
                  <input type="text" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light" placeholder="State" data-testid="shipping-state" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">ZIP Code</label>
                  <input type="text" value={address.zip} onChange={e => setAddress(p => ({ ...p, zip: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light" placeholder="10001" data-testid="shipping-zip" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Country</label>
                  <input type="text" value={address.country} onChange={e => setAddress(p => ({ ...p, country: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light" placeholder="US" data-testid="shipping-country" />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="border border-red-500/30 bg-red-500/5 p-4 text-red-400 text-sm font-light" data-testid="checkout-error">{error}</div>
          )}
        </div>

        {/* Order summary */}
        <div className="lg:col-span-1">
          <div className="bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8 sticky top-28" data-testid="checkout-summary">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-[#FDFBF7] mb-6">Your Order</h3>
            <div className="space-y-4 mb-6">
              {cart.items.map(item => (
                <div key={item.product_id} className="flex justify-between items-start text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="text-[#FDFBF7] font-light truncate">{item.product?.name}</p>
                    <p className="text-[#A1A1AA] text-xs font-light">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-[#FDFBF7] font-light ml-4">${(item.product?.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <Separator className="bg-white/5 my-6" />

            {/* Promo code */}
            <div className="mb-6">
              {promo ? (
                <div className="flex items-center justify-between bg-[#0a0a0a] border border-[#D4AF37]/30 px-4 py-3" data-testid="applied-promo">
                  <div>
                    <p className="text-xs text-[#D4AF37] font-mono tracking-wider">{promo.code}</p>
                    <p className="text-[10px] text-[#A1A1AA] font-light mt-0.5">
                      {promo.type === "percent" ? `${promo.value}% off applied` : `$${promo.value.toFixed(2)} off applied`}
                    </p>
                  </div>
                  <button onClick={() => { setPromo(null); setPromoError(""); }} className="text-[#A1A1AA] hover:text-red-400 text-xs transition-colors" data-testid="remove-promo-btn">
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                    onKeyDown={e => e.key === "Enter" && applyPromo()}
                    placeholder="Promo code"
                    className="flex-1 bg-[#0a0a0a] border border-white/10 px-4 py-2.5 text-xs text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37]/40 transition-colors duration-300 font-mono tracking-wider uppercase"
                    data-testid="promo-code-input"
                  />
                  <button
                    onClick={applyPromo}
                    disabled={promoLoading || !promoInput.trim()}
                    className="border border-[#D4AF37]/40 text-[#D4AF37] px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#D4AF37]/10 transition-colors duration-300 disabled:opacity-40"
                    data-testid="apply-promo-btn"
                  >
                    {promoLoading ? "..." : "Apply"}
                  </button>
                </div>
              )}
              {promoError && <p className="text-red-400 text-xs font-light mt-2" data-testid="promo-error">{promoError}</p>}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1AA] font-light">Subtotal</span>
                <span className="text-[#FDFBF7] font-light">${cart.total?.toFixed(2)}</span>
              </div>
              {promo && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#A1A1AA] font-light">Discount ({promo.code})</span>
                  <span className="text-[#D4AF37] font-light" data-testid="checkout-discount-value">−${promo.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1AA] font-light">Shipping</span>
                <span className={summary && summary.shipping === 0 ? "text-[#D4AF37] font-light" : "text-[#FDFBF7] font-light"} data-testid="checkout-shipping-value">
                  {summary ? (summary.shipping === 0 ? "Free" : `$${summary.shipping.toFixed(2)}`) : "—"}
                </span>
              </div>
              {summary && summary.amount_to_free_shipping > 0 && (
                <p className="text-xs text-[#A1A1AA] font-light" data-testid="free-shipping-nudge">
                  Add <span className="text-[#D4AF37]">${summary.amount_to_free_shipping.toFixed(2)}</span> more for free shipping
                </p>
              )}
              <Separator className="bg-white/5" />
              <div className="flex justify-between">
                <span className="text-sm uppercase tracking-wide text-[#FDFBF7] font-medium">Total</span>
                <span className="text-xl text-[#D4AF37] font-['Cormorant_Garamond'] font-light" data-testid="checkout-total-value">
                  ${summary ? summary.total.toFixed(2) : cart.total?.toFixed(2)}
                </span>
              </div>
            </div>
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="btn-luxury w-full bg-[#D4AF37] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500 mt-8 disabled:opacity-50"
              data-testid="place-order-btn"
            >
              {loading ? "Processing..." : "Place Order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

